import { AriaMenuOptions, useMenu } from "@react-aria/menu";
import { mergeProps } from "@react-aria/utils";
import { useTreeState } from "@react-stately/tree";
import { DOMAttributes } from "@react-types/shared";
import React, {
  ElementRef,
  ForwardedRef,
  ReactElement,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import styled from "styled-components";
import { tombac } from "../../shared";
import {
  PropsWithPropStyling,
  extractStylingProps,
  propStyling,
} from "../../shared/propStyling";
import { MenuItemRenderer } from "./MenuItem";

export type MenuRootProps<T extends object> = PropsWithPropStyling<
  DOMAttributes<HTMLUListElement> &
    AriaMenuOptions<T> & {
      className?: string;
      children: ReactElement | ReactElement[];
    }
>;

const MenuRootWithRef = forwardRef(MenuRoot) as <T extends object>(
  props: MenuRootProps<T> & {
    ref?: ForwardedRef<ElementRef<"ul">>;
  },
) => ReturnType<typeof MenuRoot>;

export { MenuRootWithRef as MenuRoot };

function MenuRoot<T extends object>(
  {
    autoFocus,
    children,
    isVirtualized,
    keyboardDelegate,
    onAction,
    onClose,
    onKeyDown,
    onKeyUp,
    shouldFocusWrap,
    ...restProps
  }: MenuRootProps<T>,
  forwardedRef: ForwardedRef<HTMLUListElement>,
) {
  const menuRef = useRef<HTMLUListElement>(null);
  useImperativeHandle(forwardedRef, () => menuRef.current!, [menuRef.current]);

  const [stylingProps, componentProps] = extractStylingProps(restProps);

  const treeState = useTreeState<T>({ children });
  const { menuProps } = useMenu(
    {
      autoFocus,
      isVirtualized,
      keyboardDelegate,
      onAction,
      onClose,
      onKeyDown,
      onKeyUp,
      shouldFocusWrap,
    },
    treeState,
    menuRef,
  );

  return (
    <Container
      {...stylingProps}
      {...mergeProps(componentProps, menuProps)}
      ref={menuRef}
    >
      {[...treeState.collection].map((item) => (
        <MenuItemRenderer key={item.key} item={item} treeState={treeState} />
      ))}
    </Container>
  );
}

const Container = styled.ul`
  all: initial;
  background-color: ${tombac.color.white};
  border-radius: ${tombac.unit(5)};
  box-shadow: ${tombac.unit(0, 1, 3, 0)} ${tombac.alpha("neutral", 16)};
  display: flex;
  flex-flow: column;
  padding: ${tombac.space(1, 0)};
  ${propStyling}
`;
